/**
 * Export helpers for the media browser.
 *
 * Two destinations:
 *   - download → save the file to disk (browser download / Tauri save-as)
 *   - share    → hand the file off to the OS share sheet via Web Share API
 *
 * Sharing is feature-detected at runtime: on Tauri desktop the WebView
 * usually does not expose `navigator.share`, in which case `isShareAvailable`
 * returns `false` and the UI just hides the option.
 *
 * Source bytes:
 *   - local items → read directly from OPFS / Tauri-local storage
 *   - cloud items → request a short-lived signed URL, then fetch it
 */

import { apiFetch } from "../../lib/api.js";
import { isTauri, type LocalMediaItem, readLocalMediaBytes } from "../../lib/local-media.js";

export interface ExportTarget {
  /** Visible filename — used by browser download attr, save dialog and Web Share. */
  filename: string;
  /** MIME — used to type the Blob / File. */
  mime: string;
  /**
   * How to fetch the raw bytes. Returns a `Uint8Array` because both the
   * Web Share `File` ctor and Tauri's `writeFile` accept that shape.
   */
  getBytes: () => Promise<Uint8Array>;
}

interface CloudViewUrlResponse {
  ok: true;
  url: string;
  expiresIn: number;
}

/* ─────────────────────── factories ─────────────────────── */

export function cloudExportTarget(item: {
  id: string;
  mime: string;
  filename: string;
}): ExportTarget {
  return {
    filename: item.filename,
    mime: item.mime,
    getBytes: async () => {
      // Cloud items are served via short-lived signed URLs; refetch every
      // time so we don't hand out a URL that's about to expire.
      const view = await apiFetch<CloudViewUrlResponse>(`/api/v1/media/${item.id}/view-url`);
      const res = await fetch(view.url);
      if (!res.ok) {
        throw new Error(`Download fehlgeschlagen (HTTP ${res.status}).`);
      }
      return new Uint8Array(await res.arrayBuffer());
    },
  };
}

export function localExportTarget(item: LocalMediaItem): ExportTarget {
  return {
    filename: item.filename,
    mime: item.mime,
    getBytes: async () => {
      const bytes = await readLocalMediaBytes(item);
      // readLocalMediaBytes already returns Uint8Array — but normalise in
      // case the underlying impl changes (OPFS variant returned ArrayBuffer
      // historically).
      return bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
    },
  };
}

/* ─────────────────────── download ─────────────────────── */

export async function downloadTarget(target: ExportTarget): Promise<void> {
  const bytes = await target.getBytes();
  if (isTauri()) {
    await saveViaTauri(target.filename, target.mime, bytes);
    return;
  }
  saveViaBrowser(target.filename, target.mime, bytes);
}

function saveViaBrowser(filename: string, mime: string, bytes: Uint8Array): void {
  const blob = new Blob([bytes.buffer as ArrayBuffer], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Give the browser a beat to start the download before revoking.
  window.setTimeout(() => URL.revokeObjectURL(url), 4_000);
}

async function saveViaTauri(filename: string, mime: string, bytes: Uint8Array): Promise<void> {
  const [{ save }, { writeFile }] = await Promise.all([
    import("@tauri-apps/plugin-dialog"),
    import("@tauri-apps/plugin-fs"),
  ]);
  // Build a single-entry filter list from the MIME's extension when we can
  // recognise it; falls back to "Alle Dateien" otherwise.
  const ext = extensionForMime(mime, filename);
  const filters = ext ? [{ name: ext.toUpperCase(), extensions: [ext] }] : undefined;
  const picked = await save({ defaultPath: filename, filters });
  if (!picked) return; // user cancelled
  await writeFile(picked, bytes);
}

/* ─────────────────────── share ─────────────────────── */

/**
 * True if the current platform exposes `navigator.share` *and* the browser
 * actually accepts a file payload. Desktop Chrome/Edge expose `share` but
 * reject `canShare({files})` — we hide the menu entry in that case rather
 * than letting the user click into a `NotAllowedError`.
 */
export function isShareAvailable(): boolean {
  if (typeof navigator === "undefined") return false;
  const nav = navigator as Navigator & {
    canShare?: (data: ShareData) => boolean;
  };
  if (typeof nav.share !== "function") return false;
  if (typeof nav.canShare !== "function") return false;
  try {
    // Probe with a tiny dummy file — cheap and matches the shape we'd
    // actually pass at share-time.
    const probe = new File([new Uint8Array(1)], "probe.bin", {
      type: "application/octet-stream",
    });
    return nav.canShare({ files: [probe] });
  } catch {
    return false;
  }
}

/**
 * Share an already-materialised File. Caller MUST invoke this directly
 * inside the click handler (no awaits in between) so the browser's
 * transient user activation isn't consumed by an interleaved fetch —
 * otherwise Chrome rejects with `NotAllowedError`.
 */
export async function shareFile(file: File): Promise<void> {
  if (typeof navigator === "undefined" || typeof navigator.share !== "function") {
    throw new Error("Teilen wird auf diesem Gerät nicht unterstützt.");
  }
  const nav = navigator as Navigator & {
    canShare?: (data: ShareData) => boolean;
  };
  if (!nav.canShare || !nav.canShare({ files: [file] })) {
    throw new Error("Diese Datei kann auf diesem Gerät nicht geteilt werden.");
  }
  await nav.share({ files: [file], title: file.name });
}

/** Build a `File` from an ExportTarget (does the fetch). */
export async function fileForTarget(target: ExportTarget): Promise<File> {
  const bytes = await target.getBytes();
  return new File([bytes.buffer as ArrayBuffer], target.filename, {
    type: target.mime,
  });
}

/* ─────────────────────── helpers ─────────────────────── */

const MIME_TO_EXT: Record<string, string> = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "image/jpg": "jpg",
  "image/webp": "webp",
  "image/avif": "avif",
  "image/gif": "gif",
  "video/mp4": "mp4",
  "video/webm": "webm",
  "video/quicktime": "mov",
  "application/pdf": "pdf",
};

function extensionForMime(mime: string, filename: string): string | null {
  const fromMap = MIME_TO_EXT[mime];
  if (fromMap) return fromMap;
  const m = /\.([a-z0-9]+)$/i.exec(filename);
  return m && m[1] ? m[1].toLowerCase() : null;
}
